/*
 * SARBatchCard — tarjeta expandible que muestra el progreso agregado de un batch.
 * Hace polling cada 3s hasta que el batch esté en `done` o `cancelled`.
 */
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { ChevronDown, ChevronRight, Loader2, Check, XCircle, MapPin, FileText, X as XIcon } from 'lucide-react';

const ROW_STATUS_META = {
  queued:    { label: 'En cola', className: 'text-white/40' },
  geocoding: { label: 'Geo…',    className: 'text-orange-300' },
  running:   { label: 'Adquiriam…',     className: 'text-blue-300' },
  done:      { label: 'Listo',   className: 'text-[#A3FF12]' },
  error:     { label: 'Error',   className: 'text-red-300' },
};

const SARBatchCard = ({ batchId, onOpenProspection, onDismiss }) => {
  const [batch, setBatch] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let errors = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/research/sar/batches/${batchId}`);
        if (cancelled) return;
        errors = 0;
        setBatch(data);
      } catch {
        errors += 1;
        if (errors >= 6) cancelled = true;
      }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [batchId]);

  if (!batch) {
    return (
      <div className="bg-black/30 border border-white/10 rounded-xl p-3 text-white/40 text-xs flex items-center gap-2">
        <Loader2 className="animate-spin" size={13}/> Cargando lote {batchId.slice(0, 8)}…
      </div>
    );
  }

  const items = batch.items || [];
  const done = items.filter((i) => i.status === 'done').length;
  const errors = items.filter((i) => i.status === 'error').length;
  const active = items.filter((i) => ['geocoding', 'running'].includes(i.status)).length;
  const total = items.length;
  const pctGlobal = Math.round((done + errors) / Math.max(1, total) * 100);
  const running = batch.status === 'running';

  const cancel = async () => {
    if (!window.confirm('¿Cancelar el resto del lote? Las filas ya iniciadas terminarán igual.')) return;
    try { await api.post(`/research/sar/batches/${batchId}/cancel`); } catch { /* ignore */ }
  };

  return (
    <div className="bg-black/40 border border-[#A3FF12]/30 rounded-xl overflow-hidden" data-testid={`batch-${batchId}`}>
      <div className="p-3 flex items-center gap-2">
        <button onClick={() => setOpen(!open)} className="text-white/50 hover:text-white flex-shrink-0">
          {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black tracking-widest text-[#A3FF12]">SAR EN LOTE</span>
            <span className="text-[10px] text-white/40">#{batchId.slice(0, 8)}</span>
            {running && <span className="text-[10px] font-bold text-blue-300 inline-flex items-center gap-1"><Loader2 className="animate-spin" size={10}/> {active} activas</span>}
            {batch.status === 'done' && <span className="text-[10px] font-black text-[#A3FF12]">COMPLETADO</span>}
            {batch.status === 'cancelled' && <span className="text-[10px] font-black text-red-300">CANCELADO</span>}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-white/60">
            <span>{done}/{total} listas</span>
            {errors > 0 && <span className="text-red-300">{errors} errores</span>}
          </div>
          <div className="mt-2 w-full h-1.5 bg-white/5 rounded-full overflow-hidden" data-testid={`batch-bar-${batchId}`}>
            <div className="h-full bg-[#A3FF12] transition-all" style={{ width: `${Math.max(3, pctGlobal)}%` }}/>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {running && (
            <button onClick={cancel} data-testid={`cancel-batch-${batchId}`}
              className="text-[10px] font-bold text-red-300 border border-red-500/30 hover:bg-red-500/10 rounded-full px-2 py-0.5">
              Cancelar
            </button>
          )}
          {!running && onDismiss && (
            <button onClick={() => onDismiss(batchId)} data-testid={`dismiss-batch-${batchId}`}
              className="text-white/40 hover:text-white p-1" title="Ocultar">
              <XIcon size={14}/>
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 divide-y divide-white/5 max-h-72 overflow-y-auto">
          {items.map((it) => (
            <BatchRow key={it.row} item={it} onOpenProspection={onOpenProspection}/>
          ))}
        </div>
      )}
    </div>
  );
};

const BatchRow = ({ item, onOpenProspection }) => {
  const meta = ROW_STATUS_META[item.status] || ROW_STATUS_META.queued;
  const isActive = ['geocoding', 'running'].includes(item.status);
  return (
    <div className="px-3 py-2 flex items-start gap-2 text-[11px]" data-testid={`batch-row-${item.row}`}>
      <span className="text-white/30 font-mono w-6 flex-shrink-0 mt-0.5">{item.row}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <MapPin size={11} className="text-[#A3FF12] flex-shrink-0"/>
          <span className="text-white truncate">{item.address}</span>
          {item.operator_code && <span className="text-[#A3FF12] font-bold flex-shrink-0">· {item.operator_code}</span>}
        </div>
        {isActive && (
          <div className="mt-1">
            <div className="text-white/50 text-[10px]">{item.stage || 'Trabajando…'}</div>
            <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mt-0.5">
              <div className="h-full bg-[#A3FF12] transition-all" style={{ width: `${Math.max(3, item.progress || 0)}%` }}/>
            </div>
          </div>
        )}
        {item.error && (
          <div className="text-red-300 text-[10px] mt-0.5">{item.error}</div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {item.status === 'done' && item.prospection_id && (
          <button onClick={() => onOpenProspection && onOpenProspection(item.prospection_id)}
            data-testid={`batch-open-prosp-${item.row}`}
            className="text-[10px] inline-flex items-center gap-1 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] px-2 py-0.5 rounded-full font-bold hover:bg-[#A3FF12]/20">
            <FileText size={10}/> Ver
          </button>
        )}
        <span className={`text-[10px] font-black uppercase tracking-wider ${meta.className} flex items-center gap-1`}>
          {item.status === 'done' && <Check size={10}/>}
          {item.status === 'error' && <XCircle size={10}/>}
          {isActive && <Loader2 className="animate-spin" size={10}/>}
          {meta.label}
        </span>
      </div>
    </div>
  );
};

export default SARBatchCard;
